import {
  communityToChoice,
  enableNsfw,
  filterCommunitySelection,
  setIsoData,
} from "@utils/app";
import {
  bareRoutePush,
  getIdFromString,
  getQueryParams,
  getQueryString,
  numToSI,
} from "@utils/helpers";
import {
  Choice,
  CrossPostParams,
  QueryParams,
  RouteDataResponse,
  StringBoolean,
} from "@utils/types";
import { InfernoNode, Component } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  CommunityView,
  CreatePost as CreatePostI,
  GetCommunity,
  GetCommunityResponse,
  LemmyHttp,
  PagedResponse,
  PostResponse,
} from "lemmy-js-client";
import { InitialFetchRequest, PostFormParams } from "@utils/types";
import { FirstLoadService, I18NextService } from "@services/index";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
  WrappedLemmyHttp,
  wrapClient,
} from "../../services/HttpService";
import { HtmlTags } from "../common/html-tags";
import { PostForm } from "./post-form";
import { getHttpBaseInternal } from "../../utils/env";
import { IRoutePropsWithFetch } from "@utils/routes";
import { simpleScrollMixin } from "../mixins/scroll-mixin";
import { toast } from "@utils/app";
import { isBrowser } from "@utils/browser";
import { NoOptionI18nKeys } from "i18next";
import { PictrsImage } from "@components/common/pictrs-image";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";
import { communityLink } from "@components/community/community-link";
import { aimooT } from "@utils/aimoo-i18n";

export interface CreatePostProps {
  communityId?: number;
  url?: string;
  title?: string;
  body?: string;
  languageId?: number;
  nsfw?: StringBoolean;
  customThumbnailUrl?: string;
  altText?: string;
}

type CreatePostData = RouteDataResponse<{
  communityResponse: GetCommunityResponse;
  initialCommunitiesRes: PagedResponse<CommunityView>;
}>;

export function getCreatePostQueryParams(source?: string): CreatePostProps {
  return getQueryParams<CreatePostProps>(
    {
      communityId: getIdFromString,
      url: (url?: string) => url,
      body: (body?: string) => body,
      languageId: getIdFromString,
      nsfw: (nsfw?: StringBoolean) => nsfw,
      customThumbnailUrl: (customThumbnailUrl?: string) => customThumbnailUrl,
      title: (title?: string) => title,
      altText: (altText?: string) => altText,
    },
    source,
  );
}

function fetchCommunitiesForOptions(client: WrappedLemmyHttp) {
  return client.listCommunities({
    limit: 30,
    sort: "active_monthly",
    type_: "all",
  });
}

function stringAsQueryParam(param?: string) {
  return (param?.length ?? 0) > 0 ? param : undefined;
}

const AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
];

function avatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_GRADIENTS.length;
  }
  return AVATAR_GRADIENTS[hash] ?? AVATAR_GRADIENTS[0];
}

interface CreatePostState {
  createPostRes: RequestState<PostResponse>;
  selectedCommunity?: CommunityView;
  selectedCommunityIsNsfw: boolean;
  initialCommunitiesRes: RequestState<PagedResponse<CommunityView>>;
  isIsomorphic: boolean;
  resetCounter: number;
}

type CreatePostPathProps = Record<string, never>;
type CreatePostRouteProps = RouteComponentProps<CreatePostPathProps> &
  CreatePostProps;
export type CreatePostFetchConfig = IRoutePropsWithFetch<
  CreatePostData,
  CreatePostPathProps,
  CreatePostProps
>;

@simpleScrollMixin
export class CreatePost extends Component<
  CreatePostRouteProps,
  CreatePostState
> {
  private isoData = setIsoData<CreatePostData>(this.context);
  state: CreatePostState = {
    createPostRes: EMPTY_REQUEST,
    initialCommunitiesRes: EMPTY_REQUEST,
    isIsomorphic: false,
    resetCounter: 0,
    selectedCommunityIsNsfw: false,
  };

  constructor(props: CreatePostRouteProps, context: object) {
    super(props, context);

    if (FirstLoadService.isFirstLoad) {
      const { communityResponse: communityRes, initialCommunitiesRes } =
        this.isoData.routeData;

      this.state = {
        ...this.state,
        initialCommunitiesRes,
        isIsomorphic: true,
      };

      if (communityRes?.state === "success") {
        this.state = {
          ...this.state,
          selectedCommunity: communityRes.data.community_view,
        };
      }
    }
  }

  async fetchCommunity({ communityId }: CreatePostProps) {
    if (communityId) {
      const res = await HttpService.client.getCommunity({
        id: communityId,
      });
      if (res.state === "success") {
        this.setState({
          selectedCommunity: res.data.community_view,
          selectedCommunityIsNsfw: res.data.community_view.community.nsfw,
        });
      }
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      const { communityId } = this.props;

      const initialCommunitiesRes = await fetchCommunitiesForOptions(
        HttpService.client,
      );

      this.setState({
        initialCommunitiesRes,
      });

      if (
        communityId?.toString() !== this.state.selectedCommunity?.community.id
      ) {
        await this.fetchCommunity({ communityId });
      } else if (!communityId) {
        this.setState({
          selectedCommunity: undefined,
        });
      }

      const locationState = this.props.history.location.state as
        | CrossPostParams
        | undefined;
      if (locationState) {
        await this.updateUrl({
          title: locationState.name,
          url: locationState.url,
          body: locationState.body,
          altText: locationState.altText,
          nsfw: locationState.nsfw,
          languageId: locationState.languageId,
          customThumbnailUrl: locationState.customThumbnailUrl,
        });
        this.setState(s => ({ resetCounter: s.resetCounter + 1 }));
      }
    }
  }

  async componentWillReceiveProps(nextProps: CreatePostRouteProps) {
    if (bareRoutePush(this.props, nextProps)) {
      this.setState(s => ({ resetCounter: s.resetCounter + 1 }));
    }
    if (this.props.communityId !== nextProps.communityId) {
      await this.fetchCommunity(nextProps);
    }
  }

  get documentTitle(): string {
    return `${I18NextService.i18n.t("create_post")} - ${
      this.isoData.siteRes?.site_view.site.name
    }`;
  }

  render() {
    const {
      selectedCommunity,
      selectedCommunityIsNsfw,
      createPostRes,
      resetCounter,
      initialCommunitiesRes,
    } = this.state;
    const {
      body,
      communityId,
      customThumbnailUrl,
      languageId,
      title,
      nsfw,
      url,
      altText,
    } = this.props;

    const params: PostFormParams = {
      name: title,
      url,
      body,
      community_id: communityId,
      custom_thumbnail: customThumbnailUrl,
      language_id: languageId,
      nsfw: nsfw === "true",
      alt_text: altText,
    };

    const { siteRes, myUserInfo, showAdultConsentModal } = this.isoData;

    const selectedCommunityChoice = selectedCommunity
      ? communityToChoice(selectedCommunity)
      : undefined;

    const communityTitle =
      selectedCommunity?.community.title ??
      selectedCommunity?.community.name ??
      undefined;
    const communityPath = selectedCommunity
      ? communityLink(selectedCommunity.community).link
      : "/";

    return (
      <div className="aimoo-create-post" key={resetCounter}>
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-create-post__inner container-lg px-3">
          <nav className="aimoo-create-post__breadcrumb" aria-label="breadcrumb">
            <Link to="/" className="aimoo-create-post__breadcrumb-link">
              {aimooT("community")}
            </Link>
            <span className="aimoo-create-post__breadcrumb-sep" aria-hidden="true">
              /
            </span>
            {selectedCommunity ? (
              <Link to={communityPath} className="aimoo-create-post__breadcrumb-link">
                {communityTitle}
              </Link>
            ) : (
              <span>{aimooT("select_community")}</span>
            )}
            <span className="aimoo-create-post__breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="aimoo-create-post__breadcrumb-current">
              {aimooT("publish_post")}
            </span>
          </nav>

          <div className="aimoo-create-post__layout">
            <section className="aimoo-create-post__main">
              <div className="aimoo-create-post__card">
                <header className="aimoo-create-post__header">
                  <div>
                    <h1 className="aimoo-create-post__title">
                      {aimooT("publish_post")}
                    </h1>
                    <p className="aimoo-create-post__subtitle">
                      {aimooT("create_post_subtitle")}
                    </p>
                  </div>
                  {(title || body || url) && (
                    <span className="aimoo-create-post__draft-badge">
                      <span className="aimoo-create-post__draft-dot" />
                      {aimooT("draft_saved")}
                    </span>
                  )}
                </header>

                <PostForm
                  aimooLayout
                  onCreate={(form, bypassNav) =>
                    handlePostCreate(this, form, bypassNav)
                  }
                  onCancel={() => handleCancelCreate(this)}
                  params={params}
                  enableNsfw={enableNsfw(siteRes)}
                  showAdultConsentModal={showAdultConsentModal}
                  allLanguages={siteRes?.all_languages}
                  siteLanguages={siteRes?.discussion_languages}
                  selectedCommunityChoice={selectedCommunityChoice}
                  onSelectCommunity={form =>
                    handleSelectedCommunityChange(this, form)
                  }
                  selectedCommunityTags={selectedCommunity?.tags}
                  initialCommunities={
                    initialCommunitiesRes.state === "success"
                      ? filterCommunitySelection(
                          initialCommunitiesRes.data.items,
                          myUserInfo,
                        )
                      : []
                  }
                  loading={createPostRes.state === "loading"}
                  myUserInfo={myUserInfo}
                  localSite={siteRes.site_view.local_site}
                  admins={siteRes.admins}
                  onBodyBlur={form => handleBodyBlur(this, form)}
                  onLanguageChange={languageId =>
                    handleLanguageChange(this, languageId)
                  }
                  onTitleBlur={form => handleTitleBlur(this, form)}
                  onUrlBlur={form => handleUrlBlur(this, form)}
                  onThumbnailUrlBlur={form =>
                    handleThumbnailUrlBlur(this, form)
                  }
                  onNsfwChange={form => handleNsfwChange(this, form)}
                  onAltTextBlur={form => handleAltTextBlur(this, form)}
                  onCopySuggestedTitle={(url, title) =>
                    handleCopySuggestedTitle(this, url, title)
                  }
                  isNsfwCommunity={selectedCommunityIsNsfw}
                />
              </div>
            </section>

            <aside className="aimoo-create-post__aside">
              {this.renderCommunityCard()}
              {this.renderTipsCard(communityPath)}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  renderCommunityCard(): InfernoNode {
    const cv = this.state.selectedCommunity;
    if (!cv) {
      return (
        <div className="aimoo-create-post__side-card">
          <h2 className="aimoo-create-post__side-title">
            {aimooT("current_community")}
          </h2>
          <p className="aimoo-create-post__side-empty">
            {aimooT("select_community_first")}
          </p>
        </div>
      );
    }

    const community = cv.community;
    const title = community.title ?? community.name;
    const initial = title.trim().charAt(0).toUpperCase() || "?";
    const path = communityLink(community).link;

    return (
      <div className="aimoo-create-post__side-card">
        <h2 className="aimoo-create-post__side-title">
          {aimooT("current_community")}
        </h2>
        <Link to={path} className="aimoo-create-post__community-link">
          {community.icon ? (
            <PictrsImage src={community.icon} type="icon" nsfw={community.nsfw} />
          ) : (
            <span
              className={`aimoo-create-post__community-avatar ${avatarGradientClass(
                community.name,
              )}`}
            >
              {initial}
            </span>
          )}
          <span className="aimoo-create-post__community-text">
            <span className="aimoo-create-post__community-name">{title}</span>
            <span className="aimoo-create-post__community-meta">
              {aimooT("members_count", {
                count: numToSI(community.subscribers),
              })}
            </span>
          </span>
        </Link>
      </div>
    );
  }

  renderTipsCard(communityPath: string): InfernoNode {
    return (
      <div className="aimoo-create-post__side-card">
        <h2 className="aimoo-create-post__side-title">
          {aimooT("posting_tips")}
        </h2>
        <ul className="aimoo-create-post__tips">
          <li>{aimooT("posting_tip_1")}</li>
          <li>{aimooT("posting_tip_2")}</li>
          <li>{aimooT("posting_tip_3")}</li>
        </ul>
        {this.state.selectedCommunity && (
          <Link to={communityPath} className="aimoo-create-post__tips-link">
            {aimooT("view_posting_guide")}
            <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>
    );
  }

  async updateUrl(props: Partial<CreatePostProps>) {
    const {
      body,
      communityId,
      customThumbnailUrl,
      languageId,
      nsfw,
      url,
      title,
      altText,
    } = {
      ...this.props,
      ...props,
    };

    const createPostQueryParams: QueryParams<CreatePostProps> = {
      body: stringAsQueryParam(body),
      communityId: communityId?.toString(),
      customThumbnailUrl: stringAsQueryParam(customThumbnailUrl),
      languageId: languageId?.toString(),
      title: stringAsQueryParam(title),
      nsfw,
      url: stringAsQueryParam(url),
      altText: stringAsQueryParam(altText),
    };

    this.props.history.replace({
      pathname: "/create_post",
      search: getQueryString(createPostQueryParams),
    });

    await this.fetchCommunity({ communityId });
  }

  static fetchInitialData = async ({
    headers,
    query: { communityId },
  }: InitialFetchRequest<
    CreatePostPathProps,
    CreatePostProps
  >): Promise<CreatePostData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );
    const data: CreatePostData = {
      initialCommunitiesRes: await fetchCommunitiesForOptions(client),
      communityResponse: EMPTY_REQUEST,
    };

    if (communityId) {
      const form: GetCommunity = {
        id: communityId,
      };

      data.communityResponse = await client.getCommunity(form);
    }

    return data;
  };
}

async function handleSelectedCommunityChange(i: CreatePost, choice: Choice) {
  await i.updateUrl({
    communityId: getIdFromString(choice?.value),
  });
}

async function handleTitleBlur(i: CreatePost, title: string) {
  await i.updateUrl({ title });
}

async function handleUrlBlur(i: CreatePost, url: string) {
  await i.updateUrl({ url });
}

async function handleBodyBlur(i: CreatePost, body: string) {
  await i.updateUrl({ body });
}

async function handleLanguageChange(
  i: CreatePost,
  languageId: number | undefined,
) {
  await i.updateUrl({ languageId });
}

async function handleNsfwChange(i: CreatePost, nsfw: StringBoolean) {
  await i.updateUrl({ nsfw });
}

async function handleThumbnailUrlBlur(
  i: CreatePost,
  customThumbnailUrl: string,
) {
  await i.updateUrl({ customThumbnailUrl });
}

async function handleAltTextBlur(i: CreatePost, altText: string) {
  await i.updateUrl({ altText });
}

async function handleCopySuggestedTitle(
  i: CreatePost,
  url: string,
  title: string,
) {
  await i.updateUrl({ url, title });
}

function handleCancelCreate(i: CreatePost) {
  const community = i.state.selectedCommunity?.community;
  if (community) {
    i.props.history.push(communityLink(community).link);
  } else {
    i.props.history.push("/");
  }
}

async function handlePostCreate(
  i: CreatePost,
  form: CreatePostI,
  bypassNavWarning: () => void,
) {
  i.setState({ createPostRes: LOADING_REQUEST });
  const res = await HttpService.client.createPost(form);
  i.setState({ createPostRes: res });

  if (res.state === "success") {
    const postId = res.data.post_view.post.id;
    bypassNavWarning();
    removeLocalStorageMarkdown();
    i.props.history.replace(`/post/${postId}`);
  } else if (res.state === "failed") {
    toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
  }
}
